import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber, Popconfirm, message, Space, Tag, Tabs, Divider } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useCISRecords, useCISRecordCreate, useCISRecordUpdate, useCISRecordDelete, useCISStatements, useCISStatementCreate, useCISStatementDelete, useCISReturns, useCISReturnCreate, useCISReturnUpdate, useCISReturnDelete, useCCUsers } from "../../../../api-hooks/compliance";

export default function CISTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("records");
  const [modal, setModal] = useState<{ open: boolean; rec: any; type: string }>({ open: false, rec: null, type: "" });

  const { data: users = [] } = useCCUsers(cid);
  const { data: recordsRes, isLoading: rLoading } = useCISRecords(cid, {});
  const { data: stmtRes, isLoading: sLoading } = useCISStatements(cid, {});
  const { data: returnsRes, isLoading: retLoading } = useCISReturns(cid);

  const records = (recordsRes as any)?.data ?? [];
  const stmts = (stmtRes as any)?.data ?? [];
  const returns = (returnsRes as any)?.data ?? [];

  const cb = (msg: string) => ({ onSuccess: () => { msgApi.success(msg); setModal({ open: false, rec: null, type: "" }); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const rCreate = useCISRecordCreate(cid, cb("CIS record created."));
  const rUpdate = useCISRecordUpdate(cid, cb("Updated."));
  const rDelete = useCISRecordDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });
  const sCreate = useCISStatementCreate(cid, cb("Statement created."));
  const sDelete = useCISStatementDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });
  const retCreate = useCISReturnCreate(cid, cb("Monthly return created."));
  const retUpdate = useCISReturnUpdate(cid, cb("Updated."));
  const retDelete = useCISReturnDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  function openModal(type: string, rec?: any) {
    setModal({ open: true, rec: rec ?? null, type });
    const vals: any = rec ? { ...rec } : {};
    if (rec?.payment_date) vals.payment_date = dayjs(rec.payment_date);
    if (rec?.submitted_at) vals.submitted_at = dayjs(rec.submitted_at);
    form.setFieldsValue(vals);
  }

  const recordCols = [
    { title: "Subcontractor", dataIndex: "subcontractor_name", key: "s" },
    { title: "Tax Year", dataIndex: "tax_year", key: "ty" },
    { title: "Month", dataIndex: "month", key: "m" },
    { title: "Gross", dataIndex: "gross_payment", key: "g", render: (v: number) => `£${Number(v).toFixed(2)}` },
    { title: "Deduction", dataIndex: "deduction_amount", key: "d", render: (v: number) => `£${Number(v).toFixed(2)}` },
    { title: "Net", dataIndex: "net_payment", key: "n", render: (v: number) => `£${Number(v).toFixed(2)}` },
    { title: "Pay Date", dataIndex: "payment_date", key: "pd", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "", key: "act", render: (_: any, r: any) => <Space size="small"><Button size="small" onClick={() => openModal("record", r)}>Edit</Button><Popconfirm title="Delete?" onConfirm={() => rDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm></Space> },
  ];

  const stmtCols = [
    { title: "Subcontractor", dataIndex: "subcontractor_name", key: "s" },
    { title: "Tax Year", dataIndex: "tax_year", key: "ty" },
    { title: "Total Gross", dataIndex: "total_gross", key: "g", render: (v: number) => `£${Number(v).toFixed(2)}` },
    { title: "Total Deductions", dataIndex: "total_deductions", key: "d", render: (v: number) => `£${Number(v).toFixed(2)}` },
    { title: "Total Net", dataIndex: "total_net", key: "n", render: (v: number) => `£${Number(v).toFixed(2)}` },
    { title: "Sent", dataIndex: "sent_at", key: "sent", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : <Tag>Not sent</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => <Popconfirm title="Delete?" onConfirm={() => sDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm> },
  ];

  const returnsCols = [
    { title: "Tax Month", dataIndex: "tax_month", key: "tm" },
    { title: "Gross", dataIndex: "total_gross", key: "g", render: (v: number) => `£${Number(v).toFixed(2)}` },
    { title: "Deductions", dataIndex: "total_deductions", key: "d", render: (v: number) => `£${Number(v).toFixed(2)}` },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={{ draft: "default", submitted: "blue", accepted: "green", rejected: "red" }[v]}>{v?.toUpperCase()}</Tag> },
    { title: "HMRC Ref", dataIndex: "hmrc_reference", key: "r", render: (v: string) => v || "—" },
    { title: "", key: "act", render: (_: any, r: any) => <Space size="small"><Button size="small" onClick={() => openModal("return", r)}>Edit</Button><Popconfirm title="Delete?" onConfirm={() => retDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm></Space> },
  ];

  return (
    <div>
      {ctx}
      <Tabs activeKey={activeTab} onChange={setActiveTab} size="small" items={[
        { key: "records", label: "CIS Records", children: (
          <>
            <div className="flex justify-end mb-2"><Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openModal("record")} style={{ background: "#4c1d95" }}>Add Record</Button></div>
            <Table rowKey="id" dataSource={records} columns={recordCols} loading={rLoading} size="small" pagination={{ pageSize: 20 }} />
          </>
        )},
        { key: "statements", label: "Deduction Statements", children: (
          <>
            <div className="flex justify-end mb-2"><Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openModal("statement")} style={{ background: "#4c1d95" }}>Add Statement</Button></div>
            <Table rowKey="id" dataSource={stmts} columns={stmtCols} loading={sLoading} size="small" pagination={{ pageSize: 20 }} />
          </>
        )},
        { key: "returns", label: "Monthly Returns", children: (
          <>
            <div className="flex justify-end mb-2"><Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openModal("return")} style={{ background: "#4c1d95" }}>Add Return</Button></div>
            <Table rowKey="id" dataSource={returns} columns={returnsCols} loading={retLoading} size="small" pagination={{ pageSize: 20 }} />
          </>
        )},
      ]} />

      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null, type: "" })} footer={null} destroyOnClose
        title={{ record: modal.rec ? "Edit CIS Record" : "New CIS Record", statement: "New CIS Statement", return: modal.rec ? "Edit CIS Return" : "New Monthly Return" }[modal.type] || ""} width={480}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          if (modal.type === "record") {
            const p = { ...vals, payment_date: vals.payment_date?.format("YYYY-MM-DD") };
            if (modal.rec) rUpdate.mutate({ id: modal.rec.id, ...p }); else rCreate.mutate(p);
          } else if (modal.type === "statement") {
            sCreate.mutate(vals);
          } else if (modal.type === "return") {
            const p = { ...vals, submitted_at: vals.submitted_at?.toISOString() };
            if (modal.rec) retUpdate.mutate({ id: modal.rec.id, ...p }); else retCreate.mutate(p);
          }
        }}>
          {modal.type === "record" && <>
            <div className="grid grid-cols-2 gap-x-3">
              <Form.Item name="subcontractor_id" label="Subcontractor" rules={[{ required: true }]}><Select showSearch options={(users as any[]).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}`.trim() || u.email }))} /></Form.Item>
              <Form.Item name="tax_year" label="Tax Year" rules={[{ required: true }]}><Input placeholder="2025-26" /></Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-3">
              <Form.Item name="month" label="Month" rules={[{ required: true }]}><InputNumber className="w-full" min={1} max={12} /></Form.Item>
              <Form.Item name="payment_date" label="Payment Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-3">
              <Form.Item name="gross_payment" label="Gross (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} /></Form.Item>
              <Form.Item name="deduction_rate" label="Deduction Rate %" rules={[{ required: true }]}><InputNumber className="w-full" min={0} max={100} defaultValue={20} /></Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-3">
              <Form.Item name="deduction_amount" label="Deduction (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} /></Form.Item>
              <Form.Item name="net_payment" label="Net (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} /></Form.Item>
            </div>
            <Form.Item name="materials_cost" label="Materials Cost (£)"><InputNumber className="w-full" min={0} /></Form.Item>
          </>}
          {modal.type === "statement" && <>
            <div className="grid grid-cols-2 gap-x-3">
              <Form.Item name="subcontractor_id" label="Subcontractor" rules={[{ required: true }]}><Select showSearch options={(users as any[]).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}`.trim() || u.email }))} /></Form.Item>
              <Form.Item name="tax_year" label="Tax Year" rules={[{ required: true }]}><Input placeholder="2025-26" /></Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-3">
              <Form.Item name="total_gross" label="Total Gross (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} /></Form.Item>
              <Form.Item name="total_deductions" label="Total Deductions (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} /></Form.Item>
            </div>
            <Form.Item name="total_net" label="Total Net (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} /></Form.Item>
          </>}
          {modal.type === "return" && <>
            <Form.Item name="tax_month" label="Tax Month" rules={[{ required: true }]}><Input placeholder="2025-04" /></Form.Item>
            <div className="grid grid-cols-2 gap-x-3">
              <Form.Item name="total_gross" label="Total Gross (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} /></Form.Item>
              <Form.Item name="total_deductions" label="Total Deductions (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} /></Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-3">
              <Form.Item name="status" label="Status"><Select options={["draft","submitted","accepted","rejected"].map(v => ({ value: v, label: v.toUpperCase() }))} /></Form.Item>
              <Form.Item name="hmrc_reference" label="HMRC Reference"><Input /></Form.Item>
            </div>
          </>}
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={rCreate.isPending || rUpdate.isPending || sCreate.isPending || retCreate.isPending || retUpdate.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null, type: "" })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
